import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
interface FormData {
  email: string;
  type: 'user' | 'merchant' | 'enterprise';
  ageConfirm: boolean;
}

const CoalitionPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [formData, setFormData] = useState<FormData>({
    email: '',
    type: 'enterprise',
    ageConfirm: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    document.title = 'Coalition Loyalty Programs — Launch in 14 Days | Rewardly';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('article:')) {
          el.setAttribute('property', name);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Launch a branded loyalty program for your entire ecosystem in 14 days. Zero POS integration. $0 upfront. AI receipt validation. 150+ promotion templates.');
    setMeta('og:title', 'Launch a Coalition Loyalty Program in 14 Days');
    setMeta('og:description', 'One loyalty program across every merchant. No POS integration. Smart contract settlement. Patent pending.');
    setMeta('og:type', 'website');
    setMeta('og:url', 'https://rewardly.live/coalition');
    setMeta('og:image', 'https://rewardly.live/og-image.png');
    setMeta('og:site_name', 'Rewardly Protocol');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@Rewardly_io');
    setMeta('twitter:title', 'Launch a Coalition Loyalty Program in 14 Days');
    setMeta('twitter:description', 'One loyalty program across every merchant. No POS integration. Smart contract settlement. Patent pending.');
    setMeta('twitter:image', 'https://rewardly.live/og-image.png');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://rewardly.live/coalition');
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    const revealElements = document.querySelectorAll('.reveal');
    revealElements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!validateEmail(formData.email)) {
      setSubmitMessage('Please enter a valid email address');
      return;
    }
    if (!formData.ageConfirm) {
      setSubmitMessage('Please confirm you are 18 or older');
      return;
    }
    setIsSubmitting(true);
    setSubmitMessage('');
    try {
      const response = await fetch('https://5g72iuzmj9.execute-api.us-east-1.amazonaws.com/dev/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, user_type: formData.type })
      });
      const raw = await response.json();
      let data: any;
      if (raw.body && typeof raw.body === 'string') {
        try { data = JSON.parse(raw.body); } catch { data = raw; }
      } else { data = raw; }
      const statusCode = raw.statusCode || response.status;
      if (statusCode === 200 && !data.error) {
        if (data.new_subscription) {
          setSubmitMessage('Welcome! You\'re on the list. Check your email for confirmation. Follow @Rewardly_io for updates.');
        } else {
          setSubmitMessage('You\'re already on the waitlist! We\'ll email you when early access opens.');
        }
        setFormData({ email: '', type: 'user', ageConfirm: false });
        setTimeout(() => { setModalOpen(false); setSubmitMessage(''); }, 3000);
      } else {
        setSubmitMessage(data.error || 'Failed to join waitlist. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting email:', error);
      setSubmitMessage('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (submitMessage) setSubmitMessage('');
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=Outfit:wght@400;500;600;700;800&display=swap');

        :root {
          --white: #FFFFFF;
          --off-white: #F8FAFC;
          --gray-100: #F1F5F9;
          --gray-200: #E2E8F0;
          --gray-400: #94A3B8;
          --gray-500: #64748B;
          --gray-900: #0F172A;
          --blue-600: #2563EB;
          --blue-700: #1D4ED8;
          --gold: #D4A853;
          --gold-dark: #B8923A;
          --green-600: #059669;
          --red-500: #EF4444;
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }

        body {
          font-family: 'Inter', -apple-system, sans-serif;
          background: var(--white);
          color: var(--gray-900);
          line-height: 1.6;
          -webkit-font-smoothing: antialiased;
        }

        h1, h2, h3, .section-title, .stat-value, .inversion-title {
          font-family: 'Outfit', 'Inter', -apple-system, sans-serif;
        }

        .container {
          max-width: 1140px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* Hero */
        .hero {
          padding: 140px 0 80px;
          background: var(--gray-900);
          color: var(--white);
          text-align: center;
        }

        .hero-label {
          display: inline-block;
          padding: 6px 16px;
          background: rgba(212, 168, 83, 0.15);
          color: var(--gold);
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 24px;
        }

        .hero h1 {
          font-size: clamp(36px, 5vw, 52px);
          font-weight: 800;
          line-height: 1.15;
          margin-bottom: 16px;
        }

        .hero h1 .gold { color: var(--gold); }

        .hero-subtitle {
          font-family: 'Crimson Text', serif;
          font-size: 22px;
          font-style: italic;
          color: rgba(255,255,255,0.7);
          max-width: 640px;
          margin: 0 auto 40px;
          line-height: 1.6;
          letter-spacing: 0.3px;
        }

        .hero-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          max-width: 700px;
          margin: 0 auto;
        }

        .hero-stat-value {
          font-size: 36px;
          font-weight: 800;
          color: var(--gold);
        }

        .hero-stat-label {
          font-size: 14px;
          color: rgba(255,255,255,0.6);
          margin-top: 4px;
        }

        /* Sections */
        section { padding: 80px 0; }

        .section-label {
          display: inline-block;
          background: var(--blue-600);
          color: var(--white);
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
        }

        .section-title {
          font-size: clamp(28px, 4vw, 36px);
          font-weight: 800;
          margin-bottom: 16px;
        }

        .section-subtitle {
          font-size: 18px;
          color: var(--gray-500);
          max-width: 640px;
          margin: 0 auto 48px;
          line-height: 1.6;
        }

        .section-header {
          text-align: center;
          margin-bottom: 48px;
        }

        /* Competitive Layers */
        .layers-stack {
          max-width: 800px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .layer-row {
          display: flex;
          align-items: center;
          border-radius: 12px;
          padding: 24px 28px;
          background: var(--white);
          border: 1px solid var(--gray-200);
          transition: all 0.3s;
        }

        .layer-row.crowded {
          border-color: var(--red-500);
          background: rgba(239, 68, 68, 0.03);
        }

        .layer-row.blue-ocean {
          border-color: var(--gold);
          background: rgba(212, 168, 83, 0.06);
          box-shadow: 0 0 0 1px var(--gold);
          transform: scale(1.02);
        }

        .layer-row.complex {
          border-color: var(--gray-400);
          background: rgba(148, 163, 184, 0.05);
        }

        .layer-row-badge {
          flex-shrink: 0;
          width: 120px;
          text-align: center;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .layer-row.crowded .layer-row-badge {
          background: rgba(239, 68, 68, 0.1);
          color: var(--red-500);
        }

        .layer-row.blue-ocean .layer-row-badge {
          background: var(--gold);
          color: var(--gray-900);
        }

        .layer-row.complex .layer-row-badge {
          background: rgba(148, 163, 184, 0.15);
          color: var(--gray-500);
        }

        .layer-row-content {
          flex: 1;
          margin-left: 24px;
        }

        .layer-row-content h3 {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .layer-row-content p {
          font-size: 14px;
          color: var(--gray-500);
          line-height: 1.5;
        }

        .layer-row-count {
          flex-shrink: 0;
          text-align: right;
          margin-left: 24px;
        }

        .layer-row-count .count-value {
          font-size: 28px;
          font-weight: 800;
        }

        .layer-row.crowded .count-value { color: var(--red-500); }
        .layer-row.blue-ocean .count-value { color: var(--gold); }
        .layer-row.complex .count-value { color: var(--gray-400); }

        .layer-row-count .count-label {
          font-size: 12px;
          color: var(--gray-500);
        }

        /* Flywheel */
        .flywheel-section {
          background: var(--gray-900);
          color: var(--white);
        }

        .flywheel-section .section-label {
          background: rgba(212, 168, 83, 0.15);
          color: var(--gold);
        }

        .flywheel-section .section-title { color: var(--white); }
        .flywheel-section .section-subtitle { color: rgba(255,255,255,0.6); }

        .flywheel-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          max-width: 900px;
          margin: 0 auto;
        }

        .flywheel-step {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          padding: 24px;
          text-align: center;
          position: relative;
        }

        .flywheel-num {
          width: 36px;
          height: 36px;
          background: var(--gold);
          color: var(--gray-900);
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 14px;
          margin-bottom: 12px;
        }

        .flywheel-step h4 {
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 8px;
          color: var(--white);
        }

        .flywheel-step p {
          font-size: 13px;
          color: rgba(255,255,255,0.6);
          line-height: 1.5;
        }

        .flywheel-loop {
          text-align: center;
          margin-top: 24px;
          font-size: 14px;
          color: var(--gold);
          font-weight: 600;
        }

        /* Tiers */
        .tiers-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        .tier-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 12px;
          padding: 32px;
          position: relative;
        }

        .tier-card.standard {
          border-color: var(--gold);
          box-shadow: 0 0 0 1px var(--gold);
        }

        .tier-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
        }

        .tier-card:nth-child(1) .tier-badge {
          background: rgba(37, 99, 235, 0.1);
          color: var(--blue-600);
        }

        .tier-card.standard .tier-badge {
          background: var(--gold);
          color: var(--gray-900);
        }

        .tier-card:nth-child(3) .tier-badge {
          background: rgba(5, 150, 105, 0.1);
          color: var(--green-600);
        }

        .tier-card h3 {
          font-size: 22px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .tier-card .tier-desc {
          font-size: 14px;
          color: var(--gray-500);
          margin-bottom: 20px;
          line-height: 1.5;
        }

        .tier-details {
          list-style: none;
          padding: 0;
        }

        .tier-details li {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid var(--gray-100);
          font-size: 14px;
        }

        .tier-details li:last-child { border-bottom: none; }

        .tier-details .detail-label { color: var(--gray-500); }
        .tier-details .detail-value { font-weight: 600; }

        /* Treasury */
        .treasury-section {
          background: var(--off-white);
        }

        .treasury-flow {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 40px;
        }

        .treasury-node {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 10px;
          padding: 16px 20px;
          text-align: center;
          min-width: 140px;
        }

        .treasury-node strong {
          display: block;
          font-size: 14px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .treasury-node span {
          font-size: 12px;
          color: var(--gray-500);
        }

        .treasury-arrow {
          color: var(--gold);
          font-size: 20px;
          font-weight: 700;
        }

        .treasury-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          max-width: 800px;
          margin: 0 auto;
        }

        .treasury-stat {
          text-align: center;
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 10px;
          padding: 24px 16px;
        }

        .treasury-stat-value {
          font-size: 24px;
          font-weight: 800;
          color: var(--blue-600);
          margin-bottom: 4px;
        }

        .treasury-stat-label {
          font-size: 13px;
          color: var(--gray-500);
        }

        /* Launch Strategy */
        .launch-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
          margin-bottom: 40px;
        }

        .launch-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 12px;
          padding: 32px;
        }

        .launch-card .launch-label {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
        }

        .launch-card:first-child .launch-label {
          background: rgba(37, 99, 235, 0.1);
          color: var(--blue-600);
        }

        .launch-card:last-child .launch-label {
          background: rgba(5, 150, 105, 0.1);
          color: var(--green-600);
        }

        .launch-card h3 {
          font-size: 22px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .launch-card p {
          font-size: 14px;
          color: var(--gray-500);
          margin-bottom: 16px;
          line-height: 1.5;
        }

        .launch-card ul {
          list-style: none;
          padding: 0;
        }

        .launch-card li {
          padding: 6px 0;
          font-size: 14px;
          color: var(--gray-500);
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .launch-card li::before {
          content: '';
          width: 6px;
          height: 6px;
          background: var(--blue-600);
          border-radius: 50%;
          flex-shrink: 0;
        }

        .revenue-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          max-width: 700px;
          margin: 0 auto;
        }

        .revenue-card {
          text-align: center;
          background: var(--gray-900);
          border-radius: 12px;
          padding: 28px 16px;
        }

        .revenue-value {
          font-size: 28px;
          font-weight: 800;
          color: var(--gold);
          margin-bottom: 4px;
        }

        .revenue-year {
          font-size: 13px;
          color: rgba(255,255,255,0.6);
        }

        /* Promotions */
        .promos-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 32px;
        }

        .promo-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 10px;
          padding: 20px;
        }

        .promo-multiplier {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 700;
          color: var(--blue-600);
          background: rgba(37, 99, 235, 0.08);
          margin-bottom: 10px;
        }

        .promo-card h4 {
          font-size: 15px;
          font-weight: 700;
          margin-bottom: 6px;
        }

        .promo-card p {
          font-size: 13px;
          color: var(--gray-500);
          line-height: 1.4;
        }

        .promo-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          max-width: 700px;
          margin: 0 auto;
        }

        .promo-stat {
          text-align: center;
          padding: 20px 12px;
          background: var(--gray-100);
          border-radius: 10px;
        }

        .promo-stat-value {
          font-size: 22px;
          font-weight: 800;
          color: var(--gray-900);
          margin-bottom: 4px;
        }

        .promo-stat-label {
          font-size: 12px;
          color: var(--gray-500);
        }

        /* CTA */
        .cta-section {
          background: var(--gray-900);
          text-align: center;
          color: var(--white);
        }

        .cta-section h2 {
          font-size: 36px;
          font-weight: 800;
          margin-bottom: 16px;
        }

        .cta-section p {
          color: rgba(255,255,255,0.7);
          font-size: 18px;
          margin-bottom: 32px;
        }

        /* Buttons */
        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
          text-decoration: none;
        }

        .btn-gold {
          background: var(--gold);
          color: var(--gray-900);
        }

        .btn-gold:hover { background: var(--gold-dark); }

        .btn-primary {
          background: var(--blue-600);
          color: var(--white);
        }

        .btn-outline {
          background: transparent;
          border: 1px solid var(--gray-200);
          color: var(--gray-900);
        }

        /* Modal */
        .modal-overlay {
          display: none;
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 2000;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal-overlay.active { display: flex; }

        .modal {
          background: var(--white);
          border-radius: 16px;
          padding: 32px;
          max-width: 440px;
          width: 100%;
        }

        .modal h3 {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 20px;
        }

        .modal-field {
          margin-bottom: 16px;
        }

        .modal-field input, .modal-field select {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          font-size: 15px;
          font-family: inherit;
        }

        .modal-field label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 6px;
        }

        .modal-checkbox {
          display: flex;
          align-items: start;
          gap: 10px;
          margin-bottom: 20px;
          font-size: 14px;
          color: var(--gray-500);
        }

        .modal-checkbox a {
          color: var(--blue-600);
          text-decoration: none;
        }

        .modal-buttons {
          display: flex;
          gap: 12px;
        }

        .modal-buttons .btn { flex: 1; justify-content: center; }

        .submit-message {
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 16px;
          font-size: 14px;
        }

        .submit-message.success {
          background: rgba(5, 150, 105, 0.1);
          color: var(--green-600);
        }

        .submit-message.error {
          background: rgba(239, 68, 68, 0.1);
          color: var(--red-500);
        }

        .compare-table-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          max-width: 900px;
          margin: 0 auto;
        }

        /* Scroll reveal animations */
        .reveal {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        .reveal.visible {
          opacity: 1;
          transform: translateY(0);
        }

        @media (prefers-reduced-motion: reduce) {
          .reveal { opacity: 1; transform: none; transition: none; }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .hero-stats { grid-template-columns: repeat(3, 1fr); gap: 16px; }
          .hero-stat-value { font-size: 24px; }
          .layers-stack { padding: 0; }
          .layer-row { flex-direction: column; text-align: center; padding: 20px; }
          .layer-row-badge { width: auto; margin-bottom: 12px; }
          .layer-row-content { margin-left: 0; margin-bottom: 12px; }
          .layer-row-count { margin-left: 0; }
          .flywheel-grid { grid-template-columns: 1fr; }
          .tiers-grid { grid-template-columns: 1fr; }
          .treasury-flow { flex-direction: column; }
          .treasury-stats { grid-template-columns: repeat(2, 1fr); }
          .launch-grid { grid-template-columns: 1fr; }
          .revenue-row { grid-template-columns: 1fr; }
          .promos-grid { grid-template-columns: 1fr; }
          .promo-stats { grid-template-columns: repeat(2, 1fr); }
          .compare-table-grid { grid-template-columns: 1fr; }
          .reveal {
            transition-delay: 0s !important;
            transition-duration: 0.35s;
          }
        }
      `}</style>

      {/* Modal */}
      <div
        className={`modal-overlay ${modalOpen ? 'active' : ''}`}
        onClick={() => setModalOpen(false)}
      >
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h3>Join the Waitlist</h3>
          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Thank you') ? 'success' : 'error'}`}>
              {submitMessage}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="modal-field">
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="modal-field">
              <label>I am a...</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              >
                <option value="user">User</option>
                <option value="merchant">Merchant</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="modal-checkbox">
              <input
                type="checkbox"
                name="ageConfirm"
                id="ageConfirm"
                checked={formData.ageConfirm}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
              <span>I am 18 or older and agree to the <a target='_blank' rel='noopener noreferrer' href="/terms">Terms of Service and Privacy Policy</a></span>
            </div>
            <div className="modal-buttons">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
              </button>
              <button type="button" onClick={() => setModalOpen(false)} className="btn btn-outline" disabled={isSubmitting}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <Header onJoinWaitlist={() => setModalOpen(true)} />

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-label">Coalition Loyalty Platform</div>
          <h1>Launch a Loyalty Program for Your <span className="gold">Entire Ecosystem</span></h1>
          <p className="hero-subtitle">One loyalty program across every merchant in your ecosystem.<br />No POS integration required. Launch in weeks — not years.</p>
          <div className="hero-stats">
            <div>
              <div className="hero-stat-value">14 Days</div>
              <div className="hero-stat-label">Average Launch Time</div>
            </div>
            <div>
              <div className="hero-stat-value">Zero</div>
              <div className="hero-stat-label">POS Integration Required</div>
            </div>
            <div>
              <div className="hero-stat-value">$0</div>
              <div className="hero-stat-label">Upfront Software Cost</div>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section>
        <div className="container">
          <div className="section-header">
            <div className="section-label">What You Get</div>
            <h2 className="section-title">Three Layers of Value</h2>
            <p className="section-subtitle">Everything you need to run a coalition loyalty program — from branding to revenue to scale.</p>
          </div>

          <div className="layers-stack">
            <div className="layer-row blue-ocean reveal">
              <div className="layer-row-badge">Layer 1</div>
              <div className="layer-row-content">
                <h3>Your Branded Loyalty Program</h3>
                <p>Your brand, your rules. Customers earn "[Your Name] Points" at every merchant in your ecosystem. Custom promotions, branded experience, white-label app.</p>
              </div>
            </div>

            <div className="layer-row blue-ocean reveal" style={{transitionDelay: '0.1s'}}>
              <div className="layer-row-badge">Layer 2</div>
              <div className="layer-row-content">
                <h3>Treasury & Revenue Model</h3>
                <p>Fund rewards through a RWLY-backed treasury with earn-time commitment — value guaranteed at the moment customers earn, not when they redeem. Earn revenue from merchant participation and transaction fees. Smart contracts handle settlement automatically.</p>
              </div>
            </div>

            <div className="layer-row blue-ocean reveal" style={{transitionDelay: '0.2s'}}>
              <div className="layer-row-badge">Layer 3</div>
              <div className="layer-row-content">
                <h3>Cross-Coalition Interoperability</h3>
                <p>Your customers' points have value everywhere — guaranteed at earn time, redeemable across the entire RWLY network. On-chain coalition registry verifies membership instantly. Five redemption paths for your customers. More utility = more engagement = more foot traffic.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section>
        <div className="container">
          <div className="section-header">
            <div className="section-label">Use Cases</div>
            <h2 className="section-title">Built for Real Ecosystems</h2>
            <p className="section-subtitle">From malls to campuses to airports — any multi-merchant environment becomes a loyalty network.</p>
          </div>

          <div className="tiers-grid">
            <div className="tier-card standard reveal">
              <span className="tier-badge">Shopping Malls</span>
              <h3>Unified Mall Rewards</h3>
              <p className="tier-desc">Hundreds of merchants, one loyalty program. Shoppers earn at any store via receipt upload and redeem across the entire mall ecosystem.</p>
            </div>
            <div className="tier-card reveal" style={{transitionDelay: '0.05s'}}>
              <span className="tier-badge">Airlines, Hotels & Airports</span>
              <h3>Traveler Loyalty</h3>
              <p className="tier-desc">Miles multipliers, status match bonuses, direct booking rewards. Cross-property redemption across flights, hotels, and lounges.</p>
            </div>
            <div className="tier-card reveal" style={{transitionDelay: '0.1s'}}>
              <span className="tier-badge">Employers & Campuses</span>
              <h3>Employee & Campus Rewards</h3>
              <p className="tier-desc">Cafeteria, bookstore, gym — one rewards program for the entire workplace or university experience. Earn and redeem on campus.</p>
            </div>
            <div className="tier-card reveal" style={{transitionDelay: '0.15s'}}>
              <span className="tier-badge">Healthcare & Insurance</span>
              <h3>Wellness Incentives</h3>
              <p className="tier-desc">Reward preventive care, wellness checks, and healthy behavior. Points for outcomes, not just transactions. Integrated with insurance programs.</p>
            </div>
            <div className="tier-card reveal" style={{transitionDelay: '0.2s'}}>
              <span className="tier-badge">Government & Utilities</span>
              <h3>Civic Engagement</h3>
              <p className="tier-desc">Transit usage bonuses, utility payment rewards, energy savings incentives, civic participation points. Governments reward the behavior they want to see.</p>
            </div>
            <div className="tier-card reveal" style={{transitionDelay: '0.25s'}}>
              <span className="tier-badge">Non-Profits</span>
              <h3>Community & Giving</h3>
              <p className="tier-desc">Volunteer hours bonuses, donation matching, impact multipliers. Reward the act of giving — for charities, faith communities, and foundations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Not Traditional */}
      <section style={{padding: '72px 0', background: '#fff'}}>
        <div className="container">
          <div style={{textAlign: 'center', marginBottom: '48px'}}>
            <div style={{display: 'inline-block', background: 'var(--blue-600)', color: '#fff', padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px'}}>Why Rewardly</div>
            <h2 style={{fontSize: 'clamp(24px,3vw,36px)', fontWeight: 700, color: 'var(--gray-900)'}}>Traditional Coalition vs Rewardly Protocol</h2>
          </div>

          <div className="compare-table-grid">
            <div className="reveal" style={{background: 'var(--off-white)', border: '1px solid var(--gray-200)', borderRadius: '12px', padding: '32px'}}>
              <h3 style={{fontSize: '18px', fontWeight: 700, color: 'var(--gray-400)', marginBottom: '24px'}}>Traditional Coalition Setup</h3>
              <div style={{display: 'flex', flexDirection: 'column' as const, gap: '16px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '4px', paddingBottom: '12px', borderBottom: '1px solid var(--gray-200)', fontSize: '14px'}}>
                  <span style={{color: 'var(--gray-500)'}}>Launch timeline</span>
                  <span style={{fontWeight: 600, color: 'var(--gray-900)'}}>12–18 months</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '4px', paddingBottom: '12px', borderBottom: '1px solid var(--gray-200)', fontSize: '14px'}}>
                  <span style={{color: 'var(--gray-500)'}}>Integration cost</span>
                  <span style={{fontWeight: 600, color: 'var(--gray-900)'}}>$500K–2M+</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '4px', paddingBottom: '12px', borderBottom: '1px solid var(--gray-200)', fontSize: '14px'}}>
                  <span style={{color: 'var(--gray-500)'}}>Merchant onboarding</span>
                  <span style={{fontWeight: 600, color: 'var(--gray-900)'}}>POS integration per merchant</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '4px', paddingBottom: '12px', borderBottom: '1px solid var(--gray-200)', fontSize: '14px'}}>
                  <span style={{color: 'var(--gray-500)'}}>Settlement</span>
                  <span style={{fontWeight: 600, color: 'var(--gray-900)'}}>Bilateral agreements required</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '4px', fontSize: '14px'}}>
                  <span style={{color: 'var(--gray-500)'}}>Reward value</span>
                  <span style={{fontWeight: 600, color: 'var(--gray-900)'}}>Determined at redemption</span>
                </div>
              </div>
            </div>

            <div className="reveal" style={{background: 'rgba(212,168,83,0.04)', border: '2px solid var(--gold)', borderRadius: '12px', padding: '32px', transitionDelay: '0.15s'}}>
              <h3 style={{fontSize: '18px', fontWeight: 700, color: 'var(--gold-dark)', marginBottom: '24px'}}>Rewardly Protocol</h3>
              <div style={{display: 'flex', flexDirection: 'column' as const, gap: '16px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '4px', paddingBottom: '12px', borderBottom: '1px solid rgba(212,168,83,0.15)', fontSize: '14px'}}>
                  <span style={{color: 'var(--gray-500)'}}>Launch timeline</span>
                  <span style={{fontWeight: 700, color: 'var(--gold-dark)'}}>14 days</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '4px', paddingBottom: '12px', borderBottom: '1px solid rgba(212,168,83,0.15)', fontSize: '14px'}}>
                  <span style={{color: 'var(--gray-500)'}}>Integration cost</span>
                  <span style={{fontWeight: 700, color: 'var(--gold-dark)'}}>$0 upfront</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '4px', paddingBottom: '12px', borderBottom: '1px solid rgba(212,168,83,0.15)', fontSize: '14px'}}>
                  <span style={{color: 'var(--gray-500)'}}>Merchant onboarding</span>
                  <span style={{fontWeight: 700, color: 'var(--gold-dark)'}}>Zero POS — AI receipt validation</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '4px', paddingBottom: '12px', borderBottom: '1px solid rgba(212,168,83,0.15)', fontSize: '14px'}}>
                  <span style={{color: 'var(--gray-500)'}}>Settlement</span>
                  <span style={{fontWeight: 700, color: 'var(--gold-dark)'}}>One neutral token — automatic</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '4px', fontSize: '14px'}}>
                  <span style={{color: 'var(--gray-500)'}}>Reward value</span>
                  <span style={{fontWeight: 700, color: 'var(--gold-dark)'}}>Guaranteed at earn time (patent pending)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Financially */}
      <section className="treasury-section">
        <div className="container">
          <div className="section-header">
            <div className="section-label">Business Model</div>
            <h2 className="section-title">How It Works Financially</h2>
            <p className="section-subtitle">Simple economics, no reconciliation headaches.</p>
          </div>

          <div className="treasury-stats">
            <div className="treasury-stat reveal">
              <div className="treasury-stat-value">1</div>
              <div className="treasury-stat-label">Purchase a reward treasury — think pre-loaded gift card pool</div>
            </div>
            <div className="treasury-stat reveal" style={{transitionDelay: '0.08s'}}>
              <div className="treasury-stat-value">2</div>
              <div className="treasury-stat-label">Customers earn rewards from that pool when they shop</div>
            </div>
            <div className="treasury-stat reveal" style={{transitionDelay: '0.16s'}}>
              <div className="treasury-stat-value">3</div>
              <div className="treasury-stat-label">You earn revenue from merchant participation fees</div>
            </div>
            <div className="treasury-stat reveal" style={{transitionDelay: '0.24s'}}>
              <div className="treasury-stat-value">4</div>
              <div className="treasury-stat-label">38+ smart contracts across 7 layers handle settlement, conversion, and treasury automatically</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2>Launch Your Coalition</h2>
          <p>Join the waitlist to launch a loyalty program for your entire ecosystem.</p>
          <div style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap'}}>
            <button onClick={() => setModalOpen(true)} className="btn btn-gold" style={{padding: '16px 32px', fontSize: '16px'}}>Launch Your Coalition →</button>
            <a href="mailto:info@rewardly.live" style={{display: 'inline-flex', alignItems: 'center', padding: '16px 32px', fontSize: '16px', borderRadius: '8px', fontWeight: 600, textDecoration: 'none', border: '1px solid rgba(255,255,255,0.3)', color: '#FFFFFF', transition: 'all 0.2s'}}>Talk to the Team →</a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoalitionPage;
